<template>
  <div class="property-detail__container">
    <BaseButton
      :text="LABEL_CONSTANTS.BACK_TO_YOUR_DETAILS"
      flat
      ripple
      class="font-weight-semi-bold d-flex back-btn back-previous__btn-link pa-0 mb-sm-5"
      icon="mdi-chevron-left"
      color="#106175"
      small
      @click="backToYourDetail"
    />
    <v-row class="mb-5">
      <v-col class="d-flex justify-space-between flex-wrap section-title">
        <div>
          <h1 class="font-28 font-weight-bold black2-color">
            Property details
          </h1>
        </div>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-semi-bold d2c-save-exit mt-2 py-3 d-flex"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="saveAndExit"
        />
      </v-col>
    </v-row>
    <ValidationObserver
      ref="observerFormPropertyDetails"
      v-slot="{ validate, invalid }"
    >
      <v-form
        ref="form"
        @submit.prevent="validate().then(() => onSubmit(invalid))"
      >
        <LayoutAddressSearch
          :propertyDetailsTextForm="B2B_HOME_PROPERTY_LABEL"
          :hideDetails="false"
          :isShowDialogContentWarningEditAddressManual="false"
          inputFindPropertyClass="mb-2"
        />
        <BaseLabel class="mt-3" :text="'Property status'" />
        <BaseSelect
          v-model="form.propertyStatus.Value"
          :hide-details="false"
          :items="propertyStatusOptions"
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Property status',
            },
          }"
          className="long-value__input mb-2"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '360px' : '100%',
          }"
          aria-label="Property status"
        />
        <div class="mb-3" v-if="form.propertyStatus.Value == 'OwnedMortgaged'">
          <BaseLabel
            :text="'Mortgage category'"
            help-text="Please select your mortgage category. We apply an introductory no claims discount of 2 years for first time buyers."
          />
          <BaseSelect
            v-model="form.residentialStatus.Value"
            :hide-details="false"
            :items="residentialStatusOptions"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Mortgage category',
              },
            }"
            @change="(val) => setFirstTimeBuyer(val)"
            className="long-value__input"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '360px' : '100%',
            }"
            aria-label="Mortgage category"
          />

          <BaseLabel
            text="Is this insurance for a property that is subject to a new mortgage application?"
            helpText="If you are applying for a new mortgage or a remortgage to buy the property you are insuring, then please answer Yes to this question. <br/><br/>If you already have a mortgage approved for this property or own the property outright, you must answer No to this question."
            class="mt-4 mb-2"
          />
          <BaseRadio
            v-model="form.unapprovedMortgageApplication.Value"
            pslStyle="button"
            :rules="{
              required: {
                required: true,
                fieldLabel:
                  'Is this insurance for a property that is subject to a new mortgage application?',
              },
            }"
            class="pt-3 payment-method-radio"
            data-uiid="UnapprovedMortgageApplication"
            :hideDetails="false"
            :height="$vuetify.breakpoint.mdAndUp ? '48' : '60'"
            :width="$vuetify.breakpoint.mdAndUp ? '110' : 'calc(50% - 8px)'"
            :items="unapprovedMortgageApplicationItems"
            aria-label="Is this insurance for a property that is subject to a new mortgage application?"
          />
        </div>
        <div v-if="showRentalError" class="col-12 col-lg-8 pa-0 pr-lg-2 mt-4">
          <div class="bg-red-50 rounded ownership-warning">
            <div class="d-flex">
              <v-icon color="var(--red-500)" class="mr-2 align-self-start"
                >mdi-alert-circle</v-icon
              >
              <span
                ><strong>Sorry!</strong> We're unable to offer Buildings
                Insurance for this type of property. Please change your chosen
                cover type to Contents only.</span
              >
              <BaseButton
                text="Edit cover"
                flat
                link
                icon="mdi-pencil"
                depressed
                color="black--text"
                ripple
                small
                iconSize="14"
                class="d-none d-md-flex align-self-center px-1 font-weight-600 black2-color"
                @click="backToCoverTypePage"
                aria-label="Edit cover"
              />
            </div>
            <div class="d-flex justify-center">
              <BaseButton
                text="Edit cover"
                flat
                link
                icon="mdi-pencil"
                depressed
                iconSize="14"
                color="black--text"
                ripple
                small
                class="d-flex d-md-none align-self-center px-0 font-weight-600 mt-2 mt-md-0 black2-color"
                @click="backToCoverTypePage"
                aria-label="Edit cover"
              />
            </div>
          </div>
        </div>
        <BaseLabel
          class="mt-3 mb-2"
          :text="LABEL_CONSTANTS.NUMBER_OF_ADULTS"
        ></BaseLabel>
        <CounterBaseQuestion
          :answerControlCounterOptions="ANSWER_NUMBER_OF_ADULTS_COUNTER_OPTIONS"
          v-model="form.numberOfAdults.Value"
          dataUiid="NumberOfAdults"
          :className="'mb-9'"
          aria-label="Number of adults"
        />
        <BaseLabel
          class="mt-3 mb-2"
          :text="LABEL_CONSTANTS.NUMBER_OF_CHILDREN"
        ></BaseLabel>
        <CounterBaseQuestion
          :answerControlCounterOptions="
            ANSWER_NUMBER_OF_CHILDREN_COUNTER_OPTIONS
          "
          v-model="form.numberOfChildren.Value"
          dataUiid="NumberOfChildren"
          :className="'mb-9'"
          aria-label="Number of children"
        />
        <template v-if="includesBuildingCover">
          <BaseLabel
            class="mt-3 mb-2"
            :text="LABEL_CONSTANTS.HOME_NO_CLAIMS_BUILDINGS"
            :helpText="LABEL_CONSTANTS.HOME_NO_CLAIMS_BUILDINGS_HELP_TEXT"
          ></BaseLabel>
          <BaseRadio
            v-model="form.noClaimsBuildings.Value"
            pslStyle="button"
            class="mb-9"
            data-uiid="NoClaimsBuildings"
            :key="`${form.noClaimsBuildings.Value}_NoClaimsBuildings`"
            :className="'radio-group__wrapper'"
            :items="noClaimBuildingOptions"
            :rules="{
              required: {
                required: true,
                fieldLabel: LABEL_CONSTANTS.NO_CLAIMS_BUILDINGS_LABEL_MSG,
              },
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :width="$vuetify.breakpoint.lgAndUp ? '72' : '100'"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            aria-label="No claims buildings"
          />
        </template>
        <template v-if="includesContentCover">
          <BaseLabel
            class="mt-3 mb-2"
            :text="LABEL_CONSTANTS.HOME_NO_CLAIMS_CONTENTS"
            :helpText="LABEL_CONSTANTS.HOME_NO_CLAIMS_CONTENTS_HELP_TEXT"
          ></BaseLabel>
          <BaseRadio
            v-model="form.noClaimsContents.Value"
            pslStyle="button"
            class="mb-10"
            data-uiid="NoClaimsContents"
            :key="`${form.noClaimsContents.Value}_NoClaimsContents`"
            :className="'radio-group__wrapper'"
            :items="noClaimContentOptions"
            :rules="{
              required: {
                required: true,
                fieldLabel: LABEL_CONSTANTS.NO_CLAIMS_CONTENTS_LABEL_MSG,
              },
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :width="$vuetify.breakpoint.lgAndUp ? '72' : '100'"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            aria-label="No claims contents"
          />
        </template>
        <BaseButton
          rounded
          class="font-weight-bold continue-button primary--text"
          height="60"
          type="submit"
          :disabled="disableContinue"
          text="GET QUOTES"
          :color="'psorange'"
          :fontSize="'font-20'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '183px' : '100%'"
        />
      </v-form>
    </ValidationObserver>

    <EligibilityBaseDialog ref="dialog" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <Eligibility
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogLoading="$refs.dialogLoading"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :listEligibility="QUOTE_ELIGIBILITY"
          :inforMsg="QUOTE_ELIGIBILITY_INFO_MSG"
          :title="title"
          @confirmEligibility="createQuote()"
          :customHandleConfirmEligibility="true"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogLoading" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ title }">
        <EligibilityLoading
          :gettingQuoteMessage="GETTING_QUOTE_HOME_MESSAGE"
          :title="title"
          :imageSource="defaqtoImageUrl"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogNotMet" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <EligibilityNotMet
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :title="title"
        />
      </template>
    </EligibilityBaseDialog>
  </div>
</template>

<script>
import EligibilityBaseDialog from "@/components/EligibilityBaseDialog";
import Eligibility from "@/views/Dialog/Eligibility";
import EligibilityLoading from "@/views/Dialog/EligibilityLoading";
import EligibilityNotMet from "@/views/Dialog/EligibilityNotMet";
import { EventBus } from "@/common/eventbus.js";
import { ValidationObserver } from "vee-validate";
import {
  includesBuildings,
  includesContents,
  wordsToNumbers,
} from "@/common/answers";
import { mapActions, mapGetters, mapState } from "vuex";
import { convertToBool, scrollTop, getErrorMessage } from "@/common/utils";
import CounterBaseQuestion from "@/components/BaseComponents/CounterBaseQuestion.vue";
import {
  setItemDataUiids,
  NO_CLAIMS_BUILDINGS_ITEMS,
  NO_CLAIMS_CONTENTS_ITEMS,
} from "@/constants/component.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import { PROPERTY_DETAILS_FORM } from "@/constants/form.constants";
import {
  ANSWER_NUMBER_OF_ADULTS_COUNTER_OPTIONS,
  ANSWER_NUMBER_OF_CHILDREN_COUNTER_OPTIONS,
  MORTGAGE_CATEGORY_ITEMS,
  PROPERTY_STATUS_ITEMS,
  UNAPPROVED_MORTGAGE_APPLICATION_ITEMS,
} from "@/constants/property-detail.constant";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseRadio from "@/components/BaseComponents/BaseRadio.vue";
import LayoutAddressSearch from "@/components/LayoutComponents/LayoutAddressSearch.vue";
import { quoteResponseToState } from "@/common/quotes";
import {
  QUOTE_ELIGIBILITY,
  QUOTE_ELIGIBILITY_INFO_MSG,
} from "@/constants/eligibility.constants";
import { GETTING_QUOTE_HOME_MESSAGE } from "@/constants/message.constants";
import { PRICED, EXIT_DESTINATION } from "@/constants/common.constants";
import { dereference } from "@/common/transforms";
export default {
  name: "B2BHomePropertyDetailsPage",
  components: {
    ValidationObserver,
    LayoutAddressSearch,
    CounterBaseQuestion,
    BaseLabel,
    BaseSelect,
    BaseButton,
    BaseRadio,
    Eligibility,
    EligibilityBaseDialog,
    EligibilityLoading,
    EligibilityNotMet,
  },
  data() {
    return {
      defaqtoImageUrl: require("@/assets/images/defaqto/home-2024.png"),
      ROUTE_NAME,
      LABEL_CONSTANTS,
      NO_CLAIMS_CONTENTS_ITEMS,
      NO_CLAIMS_BUILDINGS_ITEMS,
      ANSWER_NUMBER_OF_ADULTS_COUNTER_OPTIONS,
      ANSWER_NUMBER_OF_CHILDREN_COUNTER_OPTIONS,
      form: PROPERTY_DETAILS_FORM,
      QUOTE_ELIGIBILITY,
      QUOTE_ELIGIBILITY_INFO_MSG,
      GETTING_QUOTE_HOME_MESSAGE,
      insuredAddress: {},
      propertyDetails: {},
      B2B_HOME_PROPERTY_LABEL: {
        PROPERTY_TYPE: "Property type",
        BUILD_YEAR: "Property build year",
        BUILD_YEAR_HELP_TEXT: LABEL_CONSTANTS.HOME_BUILD_YEAR_HELP_TEXT,
        NUMBER_OF_BEDROOMS: "Number of bedrooms",
        NUMBER_OF_BEDROOMS_HELP_TEXT:
          LABEL_CONSTANTS.NUMBER_OF_BEDROOMS_HELP_TEXT,
        NUMBER_OF_BATHROOMS: "Number of bathrooms",
        NUMBER_OF_BATHROOMS_HELP_TEXT: null,
        WALL_CONSTRUCTION: "Wall type",
        WALL_CONSTRUCTION_HELP_TEXT: null,
        ROOF_CONSTRUCTION: "Roof type",
        ROOF_CONSTRUCTION_HELP_TEXT: null,
      },
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "restoringLastStep",
      "coverAnswers",
      "questionMap",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "allSections",
      "propertyDetailsAnswer",
      "httpConfigAsRqs",
      "getQuoteRequestId",
    ]),
    isFirstTimeBuyer() {
      return this.form.residentialStatus.Value === "FirstTimeBuyer";
    },
    disableContinue() {
      return this.checkDisableContinue();
    },
    includesContentCover() {
      const coverTypeValue = this.questionMap.CoverType;
      return includesContents(coverTypeValue);
    },
    includesBuildingCover() {
      const coverTypeValue = this.questionMap.CoverType;
      return includesBuildings(coverTypeValue);
    },
    unapprovedMortgageApplicationItems() {
      return setItemDataUiids(
        "UnapprovedMortgageApplication",
        UNAPPROVED_MORTGAGE_APPLICATION_ITEMS
      );
    },
    noClaimContentOptions() {
      return setItemDataUiids("NoClaimsContents", NO_CLAIMS_CONTENTS_ITEMS);
    },
    noClaimBuildingOptions() {
      if (this.isFirstTimeBuyer) {
        NO_CLAIMS_BUILDINGS_ITEMS.forEach((item) => {
          if (item.value !== "Two") {
            item.disabled = true;
          }
        });
      } else {
        NO_CLAIMS_BUILDINGS_ITEMS.forEach((item) => (item.disabled = false));
      }
      return setItemDataUiids("NoClaimsBuildings", NO_CLAIMS_BUILDINGS_ITEMS);
    },
    propertyStatusOptions() {
      return setItemDataUiids("PropertyStatus", PROPERTY_STATUS_ITEMS);
    },
    residentialStatusOptions() {
      return setItemDataUiids("ResidentialStatus", MORTGAGE_CATEGORY_ITEMS);
    },
    showRentalError() {
      return this.checkRentalError();
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "getQuotes",
      "cloneQuote",
      "setIsExpiredFlag",
      "setFirstTimeBuyerNcd",
      "saveProgress",
      "toggleGlobalSaveAndExitDialog",
      "setCurrentRefObserverForm",
      "setRestoringLastStep",
      "setCoverAnswers",
    ]),
    saveAndExit() {
      if (
        this.form.propertyStatus &&
        this.form.propertyStatus.Value != "OwnedMortgaged"
      ) {
        this.form.residentialStatus.Value = null;
        this.form.unapprovedMortgageApplication.Value = null;
      }
      this.toggleGlobalSaveAndExitDialog(EXIT_DESTINATION.adviserHub);
    },
    async onSubmit(invalid) {
      if (invalid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.$refs.dialog.open("Eligibility check");
    },
    createQuote() {
      this.$refs.dialog.cancel();
      this.$refs.dialogLoading.open("Hang tight, getting quotes...");
      const quoteRequestFields = {
        IsPartialQuote: false,
      };

      if (this.coverAnswers?.length > 0) {
        // filter out zero answers of BuildingsCoverLevel, ExcessBuildings
        // ContentsCoverLevel,ExcessContents
        let newCoverAnswers = dereference(this.coverAnswers).filter(
          (x) =>
            x.Value !== "Zero" ||
            (x.InterfaceKey !== "BuildingsCoverLevel" &&
              x.InterfaceKey !== "ExcessBuildings" &&
              x.InterfaceKey !== "ContentsCoverLevel" &&
              x.InterfaceKey !== "ExcessContents")
        );
        newCoverAnswers = this.processContentsCoverLevel(newCoverAnswers);
        this.setCoverAnswers(newCoverAnswers);
      }

      // remove all cover answer to reduce all error relating to changing cover type from
      // BuildingsOnly to ContentsOnly
      this.getQuotes(quoteRequestFields)
        .then((quoteResponse) => {
          const newState = quoteResponseToState(quoteResponse);
          this.setIsExpiredFlag(false);
          if (newState === PRICED) {
            // Go forward if it got prices
            // Otherwise (UNABLETOQUOTE, etc.) stay on page to show error bars
            // isExpired is always false once a new quote is obtained.
            this.setIsExpiredFlag(false);
            this.$router.push({ name: ROUTE_NAME.HOME.B2B.QUOTATION_RESULT });
          } else {
            // If the quote was impossible, it's now read-only in the API side
            // and we need to create a clone for the user to fix and re-submit
            this.cloneQuote();
            scrollTop();
          }
        })
        .catch((error) => {
          getErrorMessage(error);
        })
        .finally(() => {
          if (this.$refs.dialogLoading !== undefined) {
            this.$refs.dialogLoading.cancel();
          }
          if (this.$refs.dialog !== undefined) {
            this.$refs.dialog.cancel();
          }
        });
    },
    /**
     * Filter out contents cover level value when the cover level
     * passed the maximum value configured for ownership property type
     * @param {Array} coverAnswers
     */
    processContentsCoverLevel(coverAnswers) {
      const index = coverAnswers.findIndex(
        (x) => x.InterfaceKey === "ContentsCoverLevel"
      );
      if (index === -1) {
        return coverAnswers;
      }

      const contentCoverLevelAnswer = coverAnswers[index];
      const currentOwnershipProperty = this.questionMap.OwnershipProperty.Value;
      let maximumValue, minimumValue;
      if (
        currentOwnershipProperty ===
          "RentedFromLocalAuthorityOrHousingAssociation" ||
        currentOwnershipProperty === "RentedFromPrivateLandlord"
      ) {
        maximumValue = 50000;
        minimumValue = 20000;
      } else {
        maximumValue = 100000;
        minimumValue = 75000;
      }

      const contentCoverLevel = wordsToNumbers(contentCoverLevelAnswer.Value);

      if (
        !contentCoverLevel ||
        contentCoverLevel > maximumValue ||
        contentCoverLevel < minimumValue
      ) {
        coverAnswers.splice(index, 1);
      }

      return coverAnswers;
    },
    backToCoverTypePage() {
      this.setRestoringLastStep(false);
      this.$router.push({ name: ROUTE_NAME.HOME.B2B.COVER_TYPE });
    },
    backToYourDetail() {
      this.setRestoringLastStep(false);
      this.$router.push({ name: ROUTE_NAME.HOME.B2B.YOUR_DETAILS });
    },
    initDataFromStorage() {
      // insured address
      this.insuredAddress.Street = this.questionMap.InsuredAddressStreet;
      this.insuredAddress.District = this.questionMap.InsuredAddressDistrict;
      this.insuredAddress.Town = this.questionMap.InsuredAddressTown;
      this.insuredAddress.County = this.questionMap.InsuredAddressCounty;
      this.insuredAddress.PostCode = this.questionMap.InsuredAddressPostCode;

      // property details
      this.propertyDetails.PropertyType = this.questionMap.PropertyType;
      this.propertyDetails.BuildYear = this.questionMap.BuildYear;

      this.propertyDetails.NumberOfBedrooms = this.questionMap.NumberOfBedrooms;
      if (this.propertyDetails.NumberOfBedrooms.Value === "")
        this.propertyDetails.NumberOfBedrooms.Value = "One"; //Default to "One" as the UI is set to 1 on page load

      this.propertyDetails.NumberOfBathrooms =
        this.questionMap.NumberOfBathrooms;
      if (this.propertyDetails.NumberOfBathrooms.Value === "")
        this.propertyDetails.NumberOfBathrooms.Value = "One"; //Default to "One" as the UI is set to 1 on page load

      this.propertyDetails.WallConstruction = this.questionMap.WallConstruction;
      this.propertyDetails.RoofConstruction = this.questionMap.RoofConstruction;

      this.form.windowLocksFitted = this.questionMap.WindowLocksFitted;
      if (this.form.windowLocksFitted) {
        this.form.windowLocksFitted.Value = convertToBool(
          this.form.windowLocksFitted.Value
        );
      }
      this.form.numberOfChildren = this.questionMap.NumberOfChildren;
      if (
        this.form.numberOfChildren &&
        this.form.numberOfChildren.Value == null
      ) {
        this.form.numberOfChildren.Value = "";
      }
      this.form.numberOfAdults = this.questionMap.NumberOfAdults;
      if (this.form.numberOfAdults && this.form.numberOfAdults.Value == null) {
        this.form.numberOfAdults.Value = "";
      }
      this.form.alarmType = this.questionMap.AlarmType;
      this.form.residentialStatus = this.questionMap.ResidentialStatus;
      this.form.noClaimsBuildings = this.questionMap.NoClaimsBuildings;
      this.form.propertyStatus = this.questionMap.OwnershipProperty;
      this.form.unapprovedMortgageApplication =
        this.questionMap.UnapprovedMortgageApplication;
      if (
        this.form.propertyStatus &&
        this.form.propertyStatus.Value != "OwnedMortgaged"
      ) {
        this.form.residentialStatus.Value = null;
        this.form.unapprovedMortgageApplication.Value = null;
      }
      this.form.noClaimsContents = this.questionMap.NoClaimsContents;

      this.form.findProperty = this.questionMap.FindProperty;
    },
    checkRentalError() {
      return (
        (this.form.propertyStatus.Value == "RentedFromPrivateLandlord" ||
          this.form.propertyStatus.Value ==
            "RentedFromLocalAuthorityOrHousingAssociation") &&
        includesBuildings(this.questionMap.CoverType)
      );
    },
    checkDisableContinue() {
      return (
        this.propertyDetailsAnswer.NumberOfBathrooms === "NinePlus" ||
        this.propertyDetailsAnswer.NumberOfBedrooms === "NinePlus" ||
        this.checkRentalError()
      );
    },
    setFirstTimeBuyer(val) {
      if (!val) return;
      this.setFirstTimeBuyerNcd({
        InterfaceKey: "ResidentialStatus",
        Value: val,
      });
    },
  },
  created() {
    this.initDataFromStorage();
  },
  watch: {
    "form.propertyStatus.Value"(val) {
      if (val != "OwnedMortgaged") {
        this.form.residentialStatus.Value = null;
        this.form.unapprovedMortgageApplication.Value = null;
      }
    },
  },

  async mounted() {
    scrollTop();
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPropertyDetails);
  },
};
</script>

<style lang="scss" scoped>
.ownership-warning {
  border-radius: 10px !important;
  padding: 20px !important;
  color: #272727 !important;
  margin-bottom: 36px;
  margin-top: -22px;
}
.radio-group__wrapper {
  max-width: 400px;
}
:deep(.address-finder__form) {
  .form__control {
    margin-bottom: 8px;
  }
}
</style>
